import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { AppStateService } from './app-state.service';
import { AuthService } from './auth.service';
import { AccountService } from './account.service';
import { JourneyContext } from '../models/wallpaper.model';
import { environment } from '../../environments/environment';
import { getFullAttribution } from '../utils/ga-attribution.util';

declare global {
  interface Window { dataLayer: any[]; }
}

/** Default page titles keyed by page_type */
const PAGE_TITLES: Record<string, string> = {
  homepage: 'MahattaArt - Premium Wallpapers & Wall Art',
  wallpaper_landing: 'Wallpaper Home | MahattaArt',
  listing: 'Wallpapers | MahattaArt',
  pdp: 'Wallpaper | MahattaArt',
  checkout: 'Checkout | MahattaArt',
  wishlist: 'My Wishlist | MahattaArt',
  wall_art: 'Wall Art | MahattaArt',
  art_ready: 'Art Ready | MahattaArt',
  custom_design: 'Custom Design | MahattaArt',
  print_frame: 'Print & Frame | MahattaArt',
  collage_set: 'Collage Sets | MahattaArt',
  featured_frames: 'Featured Frames | MahattaArt',
  thank_you: 'Thank You | MahattaArt',
  payment_failed: 'Payment Failed | MahattaArt',
  account: 'My Account | MahattaArt',
  about: 'About Us | MahattaArt',
  contact: 'Contact Us | MahattaArt',
  faq: 'FAQ | MahattaArt',
  privacy_policy: 'Privacy Policy | MahattaArt',
  terms_of_use: 'Terms of Use | MahattaArt',
  return_policy: 'Return Policy | MahattaArt',
  career: 'Careers | MahattaArt',
  partner: 'Partners | MahattaArt',
  find_art: 'Find Art | MahattaArt',
  art_for_business: 'Art for Business | MahattaArt',
};

@Injectable({ providedIn: 'root' })
export class DataLayerService {

  private context: JourneyContext | null = null;
  private userType: 'new' | 'existing' = 'new';
  private userCity    = '';
  private userCountry = 'India';

  private trackUrl = environment.apiUrl + '/api/unauth/gtm-event';

  constructor(
    private http: HttpClient,
    private router: Router,
    private state: AppStateService,
    private auth: AuthService,
    private account: AccountService
  ) {
    // Fetch profile when user logs in to cache city
    this.auth.user$.pipe(
      distinctUntilChanged((a, b) => !!a === !!b)
    ).subscribe(user => {
      if (user) {
        this.account.getCustomerProfile().subscribe({
          next: (res: any) => {
            const profile = res?.data?.[0] ?? res?.data ?? res;
            this.userCity    = profile?.city || '';
            this.userCountry = 'India';
          },
          error: () => {}
        });
      } else {
        this.userCity    = '';
        this.userCountry = '';
      }
    });
  }

  /** Call once from AppComponent.ngOnInit() */
  init(): void {
    window.dataLayer = window.dataLayer || [];

    // Generate a session-scoped ID once per browser tab session
    if (!sessionStorage.getItem('current_session_id')) {
      sessionStorage.setItem('current_session_id', 'S' + Date.now() + Math.random().toString(36).slice(2, 7).toUpperCase());
    }

    // Fetch IP + geo data once per session and cache in sessionStorage
    if (!sessionStorage.getItem('ip_data')) {
      this.http.get('https://ipapi.co/json/').subscribe({
        next: (res: any) => {
          const ipData = {
            ip_address: res?.ip        || null,
            city:       res?.city      || null,
            region:     res?.region    || null,
            country:    res?.country_name || null
          };
          try { sessionStorage.setItem('ip_data', JSON.stringify(ipData)); } catch {}
        },
        error: () => {}
      });
    }

    // Keep local copies in sync
    this.state.context$.subscribe(c => this.context = c);
    this.state.userType$.subscribe(t => this.userType = t);

    // Push page_view for the CURRENT page immediately (first NavigationEnd fires before init)
    const initialUrl = this.router.url;
    const initialPageType = this.resolvePageType(initialUrl);
    document.title = PAGE_TITLES[initialPageType] || 'MahattaArt';
    console.log('[GTM DataLayer] init → pushing page_view for:', initialUrl, initialPageType);
    this.pushPageView(initialUrl);

    // Push page_view on every subsequent navigation
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const url = e.urlAfterRedirects || e.url;
        const pageType = this.resolvePageType(url);
        document.title = PAGE_TITLES[pageType] || 'MahattaArt';
        console.log('[GTM DataLayer] NavigationEnd → pushing page_view for:', url, pageType);
        this.pushPageView(url);
      });
  }

  // ── Public helpers for components to call ────────────────────────

  /**
   * Components call this AFTER their data loads to set a dynamic title
   * and push an updated page_view with the correct title.
   */
  setPageTitle(title: string): void {
    document.title = title + ' | MahattaArt';
  }

  /** Get current page type from router URL */
  resolveCurrentPageType(): string {
    return this.resolvePageType(this.router.url);
  }

  /** Core push — auto-injects user_data and context_data into every event */
  private dlPush(payload: Record<string, any>): void {
    const pageType = this.resolvePageType(this.router.url);
    const fullPayload: Record<string, any> = {
      ...payload,
      user_data: this.buildUserData(),
      context_data: this.buildContextData(pageType)
    };

    const conversionEvents = ['add_to_cart', 'begin_checkout', 'purchase'];
    if (typeof payload['event'] === 'string' && conversionEvents.includes(payload['event'])) {
      const attribution = getFullAttribution();
      if (attribution['source'])   fullPayload['utm_source']   = attribution['source'];
      if (attribution['medium'])   fullPayload['utm_medium']   = attribution['medium'];
      if (attribution['campaign']) fullPayload['utm_campaign'] = attribution['campaign'];
      if (attribution['term'])     fullPayload['utm_term']     = attribution['term'];
      if (attribution['content'])  fullPayload['utm_content']  = attribution['content'];
      if (attribution['gclid'])    fullPayload['gclid']        = attribution['gclid'];
    }

    window.dataLayer.push(fullPayload);
    this.sendToBackend(fullPayload);
  }

  /** Fire-and-forget POST to backend — never throws or blocks the UI */
  private sendToBackend(payload: Record<string, any>): void {
    const event = payload['event'];
    if (event === 'page_view') return;
    if (typeof event === 'string' && event.startsWith('whatsapp_trigger_')) return;

    const body: Record<string, any> = {
      event_name: event,
      ...payload,
      page_data: payload['page_data'] || {
        page_type: this.resolvePageType(this.router.url),
        page_url: window.location.href,
        referrer: document.referrer
      }
    };

    // Attach full attribution for conversion events — backend needs ga_session_id for GA4 Realtime
    // and gclid for Google Ads. Using getFullAttribution() instead of getGaAttribution() so that
    // ga_session_id (from _ga_XXXX cookie) is included; without it GA4 MP events don't appear in Realtime.
    const conversionEvents = ['add_to_cart', 'begin_checkout', 'purchase'];
    if (typeof event === 'string' && conversionEvents.includes(event)) {
      const attribution = getFullAttribution();
      if (attribution['gclid'])          body['gclid']          = attribution['gclid'];
      if (attribution['ga_client_id'])   body['ga_client_id']   = attribution['ga_client_id'];
      if (attribution['ga_session_id'])  body['ga_session_id']  = attribution['ga_session_id'];
      if (attribution['source'])         body['utm_source']     = attribution['source'];
      if (attribution['medium'])         body['utm_medium']     = attribution['medium'];
      if (attribution['campaign'])       body['utm_campaign']   = attribution['campaign'];
      if (attribution['term'])           body['utm_term']       = attribution['term'];
      if (attribution['content'])        body['utm_content']    = attribution['content'];
      if (attribution['utm_id'])         body['utm_id']         = attribution['utm_id'];
      if (attribution['ip_address'])     body['ip_address']     = attribution['ip_address'];
      if (attribution['user_agent'])     body['user_agent']     = attribution['user_agent'];
    }

    this.http.post(this.trackUrl, body).subscribe({ error: () => {} });
  }

  /** Push any custom event */
  push(event: string, data: Record<string, any> = {}): void {
    this.dlPush({ event, ...data });
  }

  /** Push wallpaper_homepage_view — call from HomeComponent.ngOnInit */
  pushHomepageView(): void {
    const payload = {
      event: 'wallpaper_homepage_view',
      page_data: {
        page_type: 'homepage',
        page_title: document.title,
        page_url: window.location.href
      },
      event_data: {
        device: this.getDeviceType(),
        entry_source: this.getEntrySource()
      }
    };
    console.log('[GTM DataLayer] wallpaper_homepage_view:', payload);
    this.dlPush(payload);
  }

  /** Push wallpaper_landing page_view — call from LandingComponent.ngOnInit */
  pushWallpaperLandingView(): void {
    const payload = {
      event: 'page_view',
      page_data: {
        page_type: 'wallpaper_landing',
        page_title: 'Wallpaper',
        page_url: window.location.href
      },
      event_data: {
        device: this.getDeviceType(),
        entry_source: this.getEntrySource()
      }
    };
    console.log('[GTM DataLayer] wallpaper_landing view:', payload);
    this.dlPush(payload);
  }

  /** A3 — wallpaper_section_click: fires on Shop by Style / Shop by Space click */
  pushSectionClick(section: { type: string; value: string; index: number; source: string }): void {
    const payload = {
      event: 'wallpaper_section_click',
      event_data: {
        section_type: section.type,
        section_value: section.value,
        index: section.index,
        source: section.source
      }
    };
    console.log('[GTM DataLayer] wallpaper_section_click:', payload);
    this.dlPush(payload);
  }

  /** B2 — view_item_list: fires on listing page load with items, filters, sort */
  pushViewItemList(data: {
    listName: string;
    listId: string;
    items: any[];
    filters: string[];
    sortType: string;
    totalResults: number;
    categoryName?: string;
    space?: string;
    category?: string;
    sourceFunnel?: string;
    timeToLoad?: number;
  }): void {
    const fallback = data.categoryName || data.listName || '';
    const mappedItems = data.items.map((wp, i) => ({
      item_id: wp.wallpaper_id || String(wp.id),
      item_name: wp.name || '',
      item_category: this.resolveItemCategory(wp, fallback),
      price: wp.price || 0,
      index: i + 1
    }));

    // B2a — GA4 standard event
    const vilPayload = {
      event: 'view_item_list',
      ecommerce: {
        item_list_name: data.listName,
        item_list_id: data.listId,
        items: mappedItems
      },
      event_data: {
        filters_applied: data.filters,
        sort_type: data.sortType,
        total_results: data.totalResults
      }
    };
    console.log('[GTM DataLayer] view_item_list:', vilPayload);
    this.dlPush(vilPayload);

    // B2b — Custom plp_view with richer context
    const plpPayload = {
      event: 'plp_view',
      event_data: {
        page_type: 'listing',
        category: data.category || null,
        space: data.space || null,
        source_funnel: data.sourceFunnel || this.resolveSourceFunnel(),
        filters_applied: data.filters,
        sort_type: data.sortType,
        products_shown: data.items.length,
        total_results: data.totalResults,
        time_to_load: data.timeToLoad ?? null,
      }
    };
    console.log('[GTM DataLayer] plp_view:', plpPayload);
    this.dlPush(plpPayload);
  }

  /** Resolve source funnel from journey context */
  private resolveSourceFunnel(): string {
    const ctx = this.context;
    if (!ctx) return 'direct';
    if (ctx.search_query) return 'search';
    if (ctx.entry_point === 'space' || ctx.selected_space) return 'space_click';
    if (ctx.entry_point === 'category' || ctx.selected_category) return 'category_click';
    return 'direct';
  }

  /** B3 — select_item: fires when product card is clicked on PLP */
  pushSelectItem(data: {
    listName: string; listId: string; listType: string;
    source: string; item: any; index: number; categoryFallback?: string;
  }): void {
    const wp = data.item;
    const payload = {
      event: 'select_item',
      ecommerce: {
        item_list_name: data.listName,
        item_list_id: data.listId,
        items: [{
          item_id: wp.wallpaper_id || String(wp.id),
          item_name: wp.name || '',
          item_category: this.resolveItemCategory(wp, data.categoryFallback || ''),
          price: wp.price || 0,
          index: data.index
        }]
      },
      event_data: {
        source: data.source,
        list_type: data.listType,
        list_name: data.listName
      }
    };
    this.dlPush(payload);
  }

  /** B4 — filter_apply: fires when user selects a filter */
  pushFilterApply(data: {
    filterType: string; filterValue: string;
    totalActiveFilters: number; totalResults: number;
  }): void {
    this.dlPush({
      event: 'filter_apply',
      event_data: {
        filter_type: data.filterType,
        filter_value: data.filterValue,
        total_active_filters: data.totalActiveFilters,
        total_results: data.totalResults,
        page_type: 'listing'
      }
    });
  }

  /** B5 — filter_remove: fires when user deselects a filter */
  pushFilterRemove(data: {
    filterType: string; filterValue: string;
    filtersActive: string[]; totalResults: number;
  }): void {
    this.dlPush({
      event: 'filter_remove',
      event_data: {
        filter_type: data.filterType,
        filter_value: data.filterValue,
        filters_active: data.filtersActive,
        total_results: data.totalResults
      }
    });
  }

  /** B6 — filter_clear_all: fires when user clears all filters */
  pushFilterClearAll(data: {
    filtersCleared: string[]; totalResultsAfter: number;
  }): void {
    this.dlPush({
      event: 'filter_clear_all',
      event_data: {
        filters_cleared: data.filtersCleared,
        total_results_after: data.totalResultsAfter,
        page_type: 'listing'
      }
    });
  }

  /** B7 — sort_change: fires when sort option changes */
  pushSortChange(data: {
    sortType: string; previousSort: string; totalResults: number;
  }): void {
    this.dlPush({
      event: 'sort_change',
      event_data: {
        sort_type: data.sortType,
        previous_sort: data.previousSort,
        total_results: data.totalResults,
        page_type: 'listing'
      }
    });
  }

  /** B8 — plp_scroll: fires at scroll milestones */
  pushPlpScroll(data: { scrollPercent: number; timeSpent: number; filtersActive: string[] }): void {
    this.dlPush({
      event: 'plp_scroll',
      event_data: {
        scroll_percent: data.scrollPercent,
        time_spent: data.timeSpent,
        page_type: 'listing',
        filters_active: data.filtersActive
      }
    });
  }

  /** B9 — user_hesitation: fires when user spends extended time with minimal actions */
  pushUserHesitation(data: { timeSpent: number; actionsTaken: number; filtersActive: string[] }): void {
    this.dlPush({
      event: 'user_hesitation',
      event_data: {
        page_type: 'listing',
        time_spent: data.timeSpent,
        actions_taken: data.actionsTaken,
        filters_active: data.filtersActive
      }
    });
  }

  /** B10 — pagination_click */
  pushPaginationClick(data: { action: string; pageNumber: number; totalResults: number; itemsLoaded: number }): void {
    this.dlPush({
      event: 'pagination_click',
      event_data: {
        action: data.action,
        page_number: data.pageNumber,
        total_results: data.totalResults,
        items_loaded: data.itemsLoaded
      }
    });
  }

  /** B11 — add_to_wishlist from PLP */
  pushAddToWishlist(data: { item: any; source: string; categoryFallback?: string }): void {
    const wp = data.item;
    this.dlPush({
      event: 'add_to_wishlist',
      ecommerce: {
        currency: 'INR',
        value: wp.price || 0,
        items: [{
          item_id: wp.wallpaper_id || String(wp.id),
          item_name: wp.name || '',
          item_category: this.resolveItemCategory(wp, data.categoryFallback || ''),
          price: wp.price || 0,
          index: 1
        }]
      },
      event_data: { source: data.source }
    });
  }

  /** B12 — exit_intent: fires at most once per session per page_type */
  pushExitIntent(data: { timeSpent: number; filtersActive: string[]; itemsViewed: number }): void {
    const key = 'exit_intent_listing';
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    this.dlPush({
      event: 'exit_intent',
      event_data: {
        page_type: 'listing',
        time_spent: data.timeSpent,
        filters_active: data.filtersActive,
        items_viewed: data.itemsViewed
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  // SECTION C — PDP Events
  // ═══════════════════════════════════════════════════════════

  /** C1b — pdp_view: custom event with richer PDP context */
  pushPdpView(data: {
    productId: string; productName: string; category: string;
    sourceFunnel: string; spaceContext: string | null;
    price: number; hasMockup: boolean;
  }): void {
    // Track PDP visit count in session
    const key = `pdp_visit_${data.productId}`;
    const count = +(sessionStorage.getItem(key) || '0') + 1;
    sessionStorage.setItem(key, String(count));

    this.dlPush({
      event: 'pdp_view',
      event_data: {
        product_id: data.productId,
        product_name: data.productName,
        item_category: data.category,
        source_funnel: data.sourceFunnel,
        space_context: data.spaceContext,
        pdp_visit_count: count,
        price: data.price,
        has_mockup: data.hasMockup
      }
    });
  }

  /** C2 — surface_select */
  pushSurfaceSelect(data: { productId: string; surfaceType: string; previousSurface: string; priceUpdated: number | null }): void {
    this.dlPush({
      event: 'surface_select',
      event_data: {
        product_id: data.productId,
        surface_type: data.surfaceType,
        previous_surface: data.previousSurface,
        price_updated: data.priceUpdated
      }
    });
  }

  /** C3 — image_interact */
  pushImageInteract(data: { productId: string; action: string; imageIndex: number; totalImages: number }): void {
    this.dlPush({
      event: 'image_interact',
      event_data: {
        product_id: data.productId,
        action: data.action,
        image_index: data.imageIndex,
        total_images: data.totalImages
      }
    });
  }

  /** C4 — mockup_view_change */
  pushMockupViewChange(data: { productId: string; spaceFrom: string; spaceTo: string; timeToChange: number }): void {
    this.dlPush({
      event: 'mockup_view_change',
      event_data: {
        product_id: data.productId,
        space_from: data.spaceFrom,
        space_to: data.spaceTo,
        time_to_change: data.timeToChange
      }
    });
  }

  /** C5 — wall_input_start */
  pushWallInputStart(productId: string): void {
    this.dlPush({ event: 'wall_input_start', event_data: { product_id: productId, source: 'pdp' } });
  }

  /** C6 — wall_size_change */
  pushWallSizeChange(data: { productId: string; fieldChanged: string; newValue: number; unit: string }): void {
    this.dlPush({
      event: 'wall_size_change',
      event_data: {
        product_id: data.productId,
        field_changed: data.fieldChanged,
        new_value: data.newValue,
        unit: data.unit
      }
    });
  }

  /** C7 — wall_input_complete */
  pushWallInputComplete(data: { productId: string; width: number; height: number; unit: string }): void {
    this.dlPush({
      event: 'wall_input_complete',
      event_data: { width: data.width, height: data.height, unit: data.unit, product_id: data.productId }
    });
  }

  /** C8 — wall_size_error */
  pushWallSizeError(data: { productId: string; errorType: string; width: number; height: number; unit: string }): void {
    this.dlPush({
      event: 'wall_size_error',
      event_data: {
        product_id: data.productId,
        error_type: data.errorType,
        width_entered: data.width,
        height_entered: data.height,
        unit: data.unit
      }
    });
  }

  /** C9 — preview_generate */
  pushPreviewGenerate(data: { productId: string; width: number; height: number; unit: string; surfaceType: string; calculatedPrice: number }): void {
    this.dlPush({
      event: 'preview_generate',
      event_data: {
        product_id: data.productId,
        width: data.width,
        height: data.height,
        unit: data.unit,
        surface_type: data.surfaceType,
        calculated_price: data.calculatedPrice
      }
    });
  }

  /** C10 — preview_change: after initial generation, user changes dimensions/surface */
  pushPreviewChange(data: { productId: string; changeType: string; oldPrice: number; newPrice: number; changeCount: number }): void {
    this.dlPush({
      event: 'preview_change',
      event_data: {
        product_id: data.productId,
        change_type: data.changeType,
        old_price: data.oldPrice,
        new_price: data.newPrice,
        change_count: data.changeCount
      }
    });
  }

  /** C11 — price_update: fires when calculated price appears on screen */
  pushPriceUpdate(data: { productId: string; width: number; height: number; unit: string; areaSqft: number; calculatedPrice: number; pricePerSqft: number }): void {
    this.dlPush({
      event: 'price_update',
      event_data: {
        product_id: data.productId, width: data.width, height: data.height, unit: data.unit,
        area_sqft: data.areaSqft, calculated_price: data.calculatedPrice, price_per_sqft: data.pricePerSqft
      }
    });
  }

  /** C12 — add_to_wishlist from PDP */
  pushAddToWishlistPdp(data: { item: any; variant: string; category: string }): void {
    const wp = data.item;
    this.dlPush({
      event: 'add_to_wishlist',
      ecommerce: {
        currency: 'INR', value: wp.price || 0,
        items: [{
          item_id: wp.wallpaper_id || String(wp.id), item_name: wp.name || '',
          item_category: this.resolveItemCategory(wp, data.category),
          item_variant: data.variant, price: wp.price || 0
        }]
      },
      event_data: { source: 'pdp' }
    });
  }

  /** whatsapp_click — fires when user clicks the WhatsApp button (footer / floating CTA) */
  pushWhatsAppClick(active: { id?: string; name?: string; page?: string; routing?: string } | null): void {
    const pageType = this.resolvePageType(this.router.url);

    // tracking_id from URL query param, or auto-generate
    const urlParams = new URLSearchParams(window.location.search);
    const trackingId = urlParams.get('tracking_id') || (Date.now());

    // UTM / campaign data from sessionStorage
    let campaignData: Record<string, string> = {
      utm_source: '', utm_medium: '', utm_campaign: '', gclid: '', fbclid: ''
    };
    try {
      const utmRaw = sessionStorage.getItem('utm_data');
      if (utmRaw) {
        const parsed = JSON.parse(utmRaw);
        campaignData.utm_source   = parsed.utm_source   || '';
        campaignData.utm_medium   = parsed.utm_medium   || '';
        campaignData.utm_campaign = parsed.utm_campaign || '';
        campaignData.gclid        = parsed.gclid        || '';
        campaignData.fbclid       = parsed.fbclid       || '';
      }
    } catch {}

    // IP / geo from sessionStorage (fetched once in init())
    let city = '', region = '', country = '';
    try {
      const cached = sessionStorage.getItem('ip_data');
      if (cached) {
        const ip = JSON.parse(cached);
        city    = ip.city    || '';
        region  = ip.region  || '';
        country = ip.country || '';
      }
    } catch {}

    // Product context from sessionStorage (set by PDP on load)
    let productId = '', productName = '';
    try {
      const pdpCtx = sessionStorage.getItem('current_pdp_context');
      if (pdpCtx) {
        const p = JSON.parse(pdpCtx);
        productId   = p.product_id   || '';
        productName = p.product_name || '';
      }
    } catch {}

    // Use dlPush so user_data + context_data are auto-injected — same as every other GA4 event.
    // Direct window.dataLayer.push() bypasses these, causing GTM to fire "Succeeded" but GA4
    // DebugView never receives the event because required DLV variables are missing.
    this.dlPush({
      event: 'customer_support',
      event_data: {
        tracking_id:  trackingId,
        session_id:   sessionStorage.getItem('current_session_id') || '',
        city,
        region,
        country,
        utm_source:   campaignData.utm_source,
        utm_medium:   campaignData.utm_medium,
        utm_campaign: campaignData.utm_campaign,
        gclid:        campaignData.gclid,
        fbclid:       campaignData.fbclid,
        page_location: window.location.href,
        referrer:      document.referrer || '',
        page_type:     pageType.toUpperCase(),
        product_id:    productId,
        product_name:  productName,
        device_type:   window.innerWidth < 768 ? 'mobile' : 'desktop',
        trigger_id:    active?.id   ?? '',
        trigger_name:  active?.name ?? '',
      }
    });

    // this.dlPush({ event: 'customer_support', event_data: { product_id: productId, source: 'pdp' ,tracking_id:trackingId} });

    console.log('[WA] whatsapp_click pushed via dlPush');
  }

  /** C13 — add_to_cart from PDP */
  pushAddToCartPdp(data: { item: any; variant: string; category: string; quantity: number; price: number }): void {
    const wp = data.item;
    this.dlPush({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'INR', value: data.price,
        items: [{
          item_id: wp.wallpaper_id || String(wp.id), item_name: wp.name || '',
          item_category: this.resolveItemCategory(wp, data.category),
          item_brand: 'MahattaArt',
          item_variant: data.variant, price: data.price, quantity: data.quantity
        }]
      },
      event_data: this.buildCartEventData()
    });
  }

  /** Build event_data for add_to_cart — includes search context if user came from search (D2d) */
  private buildCartEventData(): Record<string, any> {
    const base: Record<string, any> = { source: 'pdp' };
    try {
      const ctx = sessionStorage.getItem('search_context');
      if (ctx) {
        const parsed = JSON.parse(ctx);
        base.source = 'search';
        base.search_term = parsed.term;
        base.search_position = parsed.position;
        base.pdp_source = 'pdp';
      }
    } catch {}
    return base;
  }

  /** C14a — recommendation_view */
  pushRecommendationView(data: { type: string; productIds: string[]; sourceProductId: string }): void {
    this.dlPush({
      event: 'recommendation_view',
      event_data: {
        recommendation_type: data.type,
        product_ids: data.productIds,
        source_product_id: data.sourceProductId
      }
    });
  }

  /** C14b — recommendation_click */
  pushRecommendationClick(data: { type: string; item: any; index: number; sourceProductId: string }): void {
    const wp = data.item;
    this.dlPush({
      event: 'recommendation_click',
      ecommerce: {
        item_list_name: data.type,
        items: [{ item_id: wp.wallpaper_id || String(wp.id), index: data.index }]
      },
      event_data: { recommendation_type: data.type, source: 'recommendation' }
    });
  }

  /** C15 — delivery_check */
  pushDeliveryCheck(data: { productId: string; pincode: string; result: string; estimatedDays: number | null }): void {
    this.dlPush({
      event: 'delivery_check',
      event_data: {
        product_id: data.productId, pincode: data.pincode,
        result: data.result, estimated_days: data.estimatedDays
      }
    });
  }

  /** C16 — share_product */
  pushShareProduct(data: { productId: string; shareMethod: string }): void {
    this.dlPush({
      event: 'share_product',
      event_data: { product_id: data.productId, share_method: data.shareMethod, page_type: 'pdp' }
    });
  }

  /** C17 — sample_request */
  pushSampleRequest(data: { productId: string; productName: string; finish: string }): void {
    const user = this.auth.currentUser;
    this.dlPush({
      event: 'sample_request',
      event_data: {
        product_id: data.productId, product_name: data.productName,
        finish: data.finish, user_login_status: user ? 'logged_in' : 'guest'
      }
    });
  }

  /** C18 — contact_cta_click */
  pushContactCtaClick(data: { productId: string; ctaType: string; sourceSection: string }): void {
    this.dlPush({
      event: 'contact_cta_click',
      event_data: { product_id: data.productId, cta_type: data.ctaType, source_section: data.sourceSection }
    });
  }

  /** C19 — user_hesitation on PDP */
  pushPdpHesitation(data: { productId: string; timeSpent: number; actionsTaken: number; priceShown: boolean; customizationDone: boolean }): void {
    this.dlPush({
      event: 'user_hesitation',
      event_data: {
        page_type: 'pdp', product_id: data.productId,
        time_spent: data.timeSpent, actions_taken: data.actionsTaken,
        price_shown: data.priceShown, customization_done: data.customizationDone
      }
    });
  }

  /** C20 — exit_intent on PDP — fires at most once per product per session */
  pushPdpExitIntent(data: { productId: string; timeSpent: number; customizationDone: boolean; priceSeen: boolean; addToCartAttempted: boolean }): void {
    const key = 'exit_intent_pdp_' + data.productId;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    this.dlPush({
      event: 'exit_intent',
      event_data: {
        page_type: 'pdp', product_id: data.productId,
        time_spent: data.timeSpent, customization_done: data.customizationDone,
        price_seen: data.priceSeen, add_to_cart_attempted: data.addToCartAttempted
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  // SECTION D — GLOBAL EVENTS (Search / Space / Category)
  // ═══════════════════════════════════════════════════════════

  /** D1 — search submit */
  pushSearch(data: { searchTerm: string; resultsCount: number; searchType: string }): void {
    console.log('[GTM DataLayer] D1 search:', data.searchTerm, 'results:', data.resultsCount);
    this.dlPush({
      event: 'search',
      search_term: data.searchTerm,
      event_data: {
        results_count: data.resultsCount,
        refinements: +(sessionStorage.getItem('search_refinement_count') || '0'),
        search_type: data.searchType
      }
    });
  }

  /** D2 — search_results_view or search_zero_result */
  pushSearchResults(data: { searchTerm: string; resultsCount: number; resultIds: string[] }): void {
    console.log('[GTM DataLayer] D2 search_results:', data.searchTerm, 'count:', data.resultsCount);
    if (data.resultsCount > 0) {
      this.dlPush({
        event: 'search_results_view',
        event_data: {
          search_term: data.searchTerm,
          results_count: data.resultsCount,
          result_ids: data.resultIds.slice(0, 10),
          has_results: true
        }
      });
    } else {
      this.dlPush({
        event: 'search_zero_result',
        event_data: { search_term: data.searchTerm, results_count: 0 }
      });
    }
  }

  /** D2b — search_refinement */
  pushSearchRefinement(data: { originalTerm: string; refinedTerm: string; previousResults: number; newResults: number }): void {
    const count = +(sessionStorage.getItem('search_refinement_count') || '0') + 1;
    sessionStorage.setItem('search_refinement_count', String(count));

    const orig = data.originalTerm.toLowerCase();
    const refined = data.refinedTerm.toLowerCase();
    let type = 'changed';
    if (refined.includes(orig)) type = 'narrowed';
    else if (orig.includes(refined)) type = 'broadened';

    this.dlPush({
      event: 'search_refinement',
      event_data: {
        original_term: data.originalTerm,
        refined_term: data.refinedTerm,
        refinement_count: count,
        previous_results: data.previousResults,
        new_results: data.newResults,
        refinement_type: type
      }
    });
  }

  /** D2c — select_item from search results (search → PDP path) */
  pushSearchSelectItem(data: { item: any; index: number; searchTerm: string }): void {
    const wp = data.item;
    this.dlPush({
      event: 'select_item',
      ecommerce: {
        item_list_name: 'search_results',
        items: [{ item_id: wp.wallpaper_id || String(wp.id), index: data.index }]
      },
      event_data: {
        source: 'search',
        search_term: data.searchTerm,
        list_type: 'search_results',
        list_name: 'Search: ' + data.searchTerm
      }
    });
    // Store search context for D2d (cart tracking)
    sessionStorage.setItem('search_context', JSON.stringify({
      term: data.searchTerm, position: data.index
    }));
  }

  // ═══════════════════════════════════════════════════════════
  // SECTION T — Smart Trigger Events
  // ═══════════════════════════════════════════════════════════

  /** Read city/region/country from IP geo cache (ipapi.co, fetched once in init()) */
  private getGeoData(): { city: string; region: string; country: string } {
    try {
      const cached = sessionStorage.getItem('ip_data');
      if (cached) {
        const ip = JSON.parse(cached);
        return {
          city:    ip.city    || '',
          region:  ip.region  || '',
          country: ip.country || '',
        };
      }
    } catch {}
    return { city: '', region: '', country: '' };
  }

  /** T — whatsapp_trigger_fired: fires whenever TriggerService fires a trigger */
  pushTriggerFired(trig: { id: string; name: string; page: string; routing: string }): void {
    const geo = this.getGeoData();
    this.dlPush({
      event: 'whatsapp_trigger_fired',
      event_data: {
        source:          trig.id,
        source_section:  trig.name,
        page_type:       trig.page,
        cta_type:        trig.routing,
        city:            geo.city,
        region:          geo.region,
        country:         geo.country,
      }
    });
  }

  /** T — whatsapp_trigger_accepted: user clicked WhatsApp CTA on the nudge */
  pushTriggerAccepted(trig: { id: string; name: string; page: string; routing: string }): void {
    const geo = this.getGeoData();
    this.dlPush({
      event: 'whatsapp_trigger_accepted',
      event_data: {
        source:         trig.id,
        source_section: trig.name,
        page_type:      trig.page,
        cta_type:       trig.routing,
        city:           geo.city,
        region:         geo.region,
        country:        geo.country,
      }
    });
  }

  /** T — whatsapp_trigger_dismissed: user manually closed the nudge */
  pushTriggerDismissed(trig: { id: string; name: string; page: string }): void {
    const geo = this.getGeoData();
    this.dlPush({
      event: 'whatsapp_trigger_dismissed',
      event_data: {
        source:         trig.id,
        source_section: trig.name,
        page_type:      trig.page,
        city:           geo.city,
        region:         geo.region,
        country:        geo.country,
      }
    });
  }

  /** D3 — space_click */
  pushSpaceClick(data: { space: string; source: string; position: string; index: number }): void {
    this.dlPush({
      event: 'space_click',
      event_data: {
        space: data.space,
        source: data.source,
        position: data.position,
        index: data.index
      }
    });
  }

  /** D4 — category_click */
  pushCategoryClick(data: { category: string; source: string; index: number }): void {
    this.dlPush({
      event: 'category_click',
      event_data: {
        category: data.category,
        source: data.source,
        index: data.index
      }
    });
  }

  /** A2 — fires view_promotion then select_promotion on banner/tile click */
  pushSelectPromotion(promo: { id: string; name: string; creative: string; slot: string; index?: number }): void {
    this.dlPush({
      event: 'view_promotion',
      ecommerce: {
        promotion_id: promo.id,
        promotion_name: promo.name,
        creative_name: promo.creative,
        creative_slot: promo.slot,
        index: promo.index ?? 0
      }
    });
    this.dlPush({
      event: 'select_promotion',
      ecommerce: {
        promotion_id: promo.id,
        promotion_name: promo.name,
        creative_name: promo.creative,
        creative_slot: promo.slot
      }
    });
  }

  /** @deprecated — view_promotion fires automatically inside pushSelectPromotion on click */
  pushViewPromotion(_promo: { id: string; name: string; creative: string; slot: string; index?: number }): void {}

  /** Detect device type */
  private getDeviceType(): 'mobile' | 'desktop' {
    return window.innerWidth < 768 ? 'mobile' : 'desktop';
  }

  /** Detect entry source from UTM / referrer */
  private getEntrySource(): string {
    try {
      const utmRaw = sessionStorage.getItem('utm_data');
      if (utmRaw) {
        const utm = JSON.parse(utmRaw);
        if (utm.utm_source && utm.utm_source !== 'direct') return utm.utm_medium || 'ad';
      }
    } catch {}
    const ref = document.referrer;
    if (!ref) return 'direct';
    if (/google|bing|yahoo|duckduckgo/i.test(ref)) return 'organic';
    return 'direct';
  }

  /** Push page_view (auto-called on NavigationEnd; components can also call manually) */
  pushPageView(url?: string): void {
    const pageUrl = url || this.router.url;
    const pageType = this.resolvePageType(pageUrl);

    const payload = {
      event: 'custom_page_view',
      page_data: {
        page_type: pageType,
        page_title: document.title,
        page_url: window.location.origin + pageUrl,
        referrer: document.referrer
      }
    };

    console.log('[GTM DataLayer] push:', JSON.stringify(payload, null, 2));
    this.dlPush(payload);
  }

  /** Push add_to_cart event */
  pushAddToCart(item: Record<string, any>): void {
    this.dlPush({
      event: 'add_to_cart',
      page_data: {
        page_type: this.resolvePageType(this.router.url),
        page_title: document.title,
        page_url: window.location.href,
        referrer: document.referrer
      },
      user_data: this.buildUserData(),
      ecommerce: { items: [item] }
    });
  }

  /** Push remove_from_cart event */
  pushRemoveFromCart(item: Record<string, any>): void {
    this.dlPush({
      event: 'remove_from_cart',
      user_data: this.buildUserData(),
      ecommerce: { items: [item] }
    });
  }

  /** Push view_item event (PDP) */
  pushViewItem(item: Record<string, any>): void {
    this.dlPush({
      event: 'view_item',
      page_data: {
        page_type: 'pdp',
        page_title: document.title,
        page_url: window.location.href,
        referrer: document.referrer
      },
      ecommerce: { items: [item] }
    });
  }

  /** Push begin_checkout event */
  pushBeginCheckout(items: Record<string, any>[]): void {
    const value = items.reduce((sum, i) => sum + ((i.price || 0) * (i.quantity || 1)), 0);
    this.dlPush({
      event: 'begin_checkout',
      ecommerce: { currency: 'INR', value, items }
    });
  }

  /** Set GA4 user_id — call once after login/signup so all subsequent hits are attributed */
  private setGa4UserId(userId: string): void {
    try {
      window.dataLayer.push({ user_id: userId });
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('set', { user_id: userId });
      }
    } catch {}
  }

  /** Push login event (GA4 standard) */
  pushLogin(user: { id?: number; customers_id?: string; firstname?: string; first_name?: string; name?: string; mobile?: string; contact?: string; phone?: string }, method: 'otp' | 'email' | 'google' = 'otp'): void {
    const userId = 'U' + String(user.customers_id || user.id || '');
    const firstName = user.firstname || user.first_name || (user.name || '').split(' ')[0] || '';
    const mobile = user.mobile || user.contact || user.phone || '';
    this.setGa4UserId(userId);
    this.dlPush({
      event: 'login',
      method,
      user_id: userId,
      first_name: firstName,
      ...(mobile ? { mobile } : {}),
      user_data: this.buildUserData(),
    });
  }

  /** Push sign_up event (GA4 standard) */
  pushSignUp(user: { id?: number; customers_id?: string; firstname?: string; first_name?: string; name?: string; mobile?: string; contact?: string; phone?: string }, method: 'otp' | 'email' | 'google' = 'otp'): void {
    const userId = 'U' + String(user.customers_id || user.id || '');
    const firstName = user.firstname || user.first_name || (user.name || '').split(' ')[0] || '';
    const mobile = user.mobile || user.contact || user.phone || '';
    this.setGa4UserId(userId);
    this.dlPush({
      event: 'sign_up',
      method,
      user_id: userId,
      first_name: firstName,
      ...(mobile ? { mobile } : {}),
      user_data: this.buildUserData(),
    });
  }

  /** Push purchase event */
  pushPurchase(_transactionId: string, _value: number, _items: Record<string, any>[]): void {
    // purchase event disabled on frontend — fire from backend only
    // this.dlPush({
    //   event: 'purchase',
    //   user_data: this.buildUserData(),
    //   ecommerce: {
    //     transaction_id: transactionId,
    //     value,
    //     currency: 'INR',
    //     items
    //   }
    // });
  }

  // ── Private helpers ──────────────────────────────────────────────

  private buildUserData(): Record<string, any> {
    const user = this.auth.currentUser;
    const data: Record<string, any> = {
      login_status: user ? 'logged_in' : 'guest',
      user_type: this.userType === 'existing' ? 'returning' : 'new',
      user_id: user ? 'U' + String(user.customers_id || user.id) : null,
      session_id: localStorage.getItem('session_id') || null,
      current_session_id: sessionStorage.getItem('current_session_id') || null
    };
    if (user) {
      if (user.email) data['email'] = user.email;
      if (user.firstname || user.lastname) {
        data['first_name'] = user.firstname || user.first_name || '';
        data['last_name']  = user.lastname  || user.last_name  || '';
      }
      const mobile = user.mobile || user.contact || user.phone || '';
      if (mobile) data['mobile'] = mobile;
      // Profile city preferred; fallback to IP geo
      const geo = this.getGeoData();
      const city    = this.userCity    || geo.city    || '';
      const country = this.userCountry || geo.country || '';
      if (city)    data['city']    = city;
      if (country) data['country'] = country;
    }
    return data;
  }

  private buildContextData(pageType: string): Record<string, any> {
    const ctx = this.context;
    let source: string | null = null;
    let sourceValue: string | null = null;

    if (ctx) {
      if (ctx.search_query) {
        source = 'search';
        sourceValue = ctx.search_query;
      } else if (ctx.selected_space) {
        source = 'space_click';
        sourceValue = ctx.selected_space;
      } else if (ctx.selected_category) {
        source = 'category_click';
        sourceValue = ctx.selected_category;
      } else if (ctx.entry_point) {
        source = ctx.entry_point;
      }
    }

    // Map internal page types to GTM current_page values
    const currentPageMap: Record<string, string> = {
      pdp: 'view_item',
      listing: 'listing',
      homepage: 'homepage',
      checkout: 'checkout',
      wallpaper_landing: 'wallpaper_landing'
    };

    return {
      space: ctx?.selected_space || null,
      category: ctx?.selected_category || null,
      source,
      source_value: sourceValue,
      current_page: currentPageMap[pageType] || pageType
    };
  }

  /** Resolve item_category from wallpaper data — always returns at least 'Wallpaper' */
  private resolveItemCategory(wp: any, fallback: string): string {
    return wp?.category_name || wp?.collection_name || fallback || wp?.collection_slug || wp?.category || 'Wallpaper';
  }

  private resolvePageType(url: string): string {
    const path = url.split('?')[0].split('#')[0];

    if (path === '/' || path === '') return 'homepage';
    if (path.startsWith('/listing')) return 'listing';
    if (path.startsWith('/wallpaper/category/')) return 'listing';
    if (path.startsWith('/wallpaper/space/')) return 'listing';
    if (path.startsWith('/wallpaper/') || path.startsWith('/pdp/')) return 'pdp';
    if (path.startsWith('/checkout') || path.startsWith('/cart')) return 'checkout';
    if (path.startsWith('/wall-art')) return 'wall_art';
    if (path.startsWith('/art-ready')) return 'art_ready';
    if (path.startsWith('/wishlist')) return 'wishlist';
    if (path.startsWith('/custom-upload') || path.startsWith('/custom-design')) return 'custom_design';
    if (path.startsWith('/print-frame')) return 'print_frame';
    if (path.startsWith('/featured-frame')) return 'featured_frames';
    if (path.startsWith('/collage-set')) return 'collage_set';
    if (path.startsWith('/thank-you') || path.startsWith('/payment-success')) return 'thank_you';
    if (path.startsWith('/payment-failed')) return 'payment_failed';
    if (path.startsWith('/account')) return 'account';
    if (path.startsWith('/wallpaper-home')) return 'wallpaper_landing';
    if (path.startsWith('/about')) return 'about';
    if (path.startsWith('/contact')) return 'contact';
    if (path.startsWith('/faq')) return 'faq';
    if (path.startsWith('/privacy-policy')) return 'privacy_policy';
    if (path.startsWith('/terms-of-use')) return 'terms_of_use';
    if (path.startsWith('/return-policy')) return 'return_policy';
    if (path.startsWith('/career')) return 'career';
    if (path.startsWith('/partner')) return 'partner';
    if (path.startsWith('/find-art')) return 'find_art';
    if (path.startsWith('/art-for-business')) return 'art_for_business';
    if (path.startsWith('/featured-art')) return 'featured_art';
    if (path.startsWith('/product-customization')) return 'product_customization';
    if (path.startsWith('/similar')) return 'similar';
    return path.replace(/^\//, '').replace(/\//g, '_') || 'other';
  }
}
